<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $tc('messages.gamification_sets', 2),
                        href: meta.links.set_index,
                    },
                    {
                        name: gamificationSet.breadcrumb_title,
                        href: meta.links.set_show,
                    },
                    {
                        name: $t('messages.edit'),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.update" method="post" @submit.prevent="submit">
            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.information') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.title')" for="title" />
                            <FormInput
                                id="title"
                                v-model="form.title"
                                name="title"
                                type="text"
                                max="191"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.title" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-3">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.base')" for="base" />
                            <FormInput
                                required
                                id="base"
                                v-model="form.base"
                                name="base"
                                type="text"
                                class="mt-1 w-full"
                                readonly
                            />
                            <FormError :value="form.errors.base" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-3">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.coins_amount_base')" for="coins_amount_base" />
                            <FormInput
                                required
                                id="coins_amount_base"
                                v-model="form.coins_amount_base"
                                name="coins_amount_base"
                                type="number"
                                min="0"
                                max="10000"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.coins_amount_base" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-3">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.health_max')" for="health_max" />
                            <FormInput
                                required
                                id="health_max"
                                v-model.number="form.health_max"
                                name="health_max"
                                type="number"
                                mix="10"
                                max="10000"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.health_max" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-3">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.ratio')" for="ratio" />
                            <FormInput
                                required
                                id="ratio"
                                v-model.number="form.ratio"
                                name="ratio"
                                type="text"
                                step="0.01"
                                min="1.00"
                                max="3.00"
                                class="mt-1 w-full"
                                readonly
                            />
                            <FormError :value="form.errors.ratio" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel
                                :value="$t('messages.students_currency_graph_days_span')"
                                for="students_currency_graph_days_span"
                            />
                            <FormInput
                                required
                                id="students_currency_graph_days_span"
                                v-model.number="form.students_currency_graph_days_span"
                                name="students_currency_graph_days_span"
                                type="number"
                                min="2"
                                max="360"
                                class="mt-1"
                            />
                            <FormError :value="form.errors.students_currency_graph_days_span" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$tc('messages.battle_terrains', 1)" for="battle_terrain_id" />
                            <BattleTerrainsSelectorPanel
                                :terrains="options.battle_terrains"
                                v-model="form.battle_terrain_id"
                                :endpoint="meta.links.terrain_store"
                            />
                            <FormError :value="form.errors.battle_terrain_id" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.class_page') }}
                </template>
                <div class="md:grid md:grid-cols-2 md:gap-6">
                    <div>
                        <div class="md:grid md:grid-cols-12 md:gap-6">
                            <div class="sm:col-span-6">
                                <div class="mt-1">
                                    <FormLabel :value="$t('messages.show_level')" for="show_level" />
                                    <input
                                        name="selected"
                                        v-model="form.show_level"
                                        type="checkbox"
                                        class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                    />
                                    <FormError :value="form.errors.show_level" class="mt-2" />
                                </div>
                            </div>

                            <div class="sm:col-span-6">
                                <div class="mt-1">
                                    <FormLabel :value="$t('messages.show_experience')" for="show_experience" />
                                    <input
                                        name="selected"
                                        v-model="form.show_experience"
                                        type="checkbox"
                                        class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                    />
                                    <FormError :value="form.errors.show_experience" class="mt-2" />
                                </div>
                            </div>

                            <div class="sm:col-span-6">
                                <div class="mt-1">
                                    <FormLabel :value="$t('messages.show_coins')" for="show_coins" />
                                    <input
                                        name="selected"
                                        v-model="form.show_coins"
                                        type="checkbox"
                                        class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                    />
                                    <FormError :value="form.errors.show_coins" class="mt-2" />
                                </div>
                            </div>

                            <div class="sm:col-span-6">
                                <div class="mt-1">
                                    <FormLabel :value="$t('messages.show_health')" for="show_health" />
                                    <input
                                        name="selected"
                                        v-model="form.show_health"
                                        type="checkbox"
                                        class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                    />
                                    <FormError :value="form.errors.show_health" class="mt-2" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div>
                        <div class="relative col-span-1 flex shadow rounded-md">
                            <div
                                class="shrink-0 flex items-center justify-center w-16 text-white text-sm leading-5 font-medium rounded-l-md overflow-hidden"
                            >
                                <span>
                                    <img src="/img/faces/student.jpg" alt="profile photo" />
                                </span>
                            </div>
                            <div
                                class="flex-1 flex flex-col justify-start border-t border-r border-b border-gray-200 bg-white rounded-r-md truncate"
                            >
                                <div class="flex flex-1 justify-between px-4 pt-1 text-sm leading-5 truncate">
                                    <span
                                        class="text-gray-900 font-medium hover:text-gray-600 transition ease-in-out duration-150"
                                    >
                                        1. {{ $t('messages.last_names') }}, {{ $t('messages.name') }}
                                    </span>
                                    <div>
                                        <div class="relative inline-block text-left">
                                            <button
                                                type="button"
                                                class="inline-flex items-center px-1.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                            >
                                                <svg
                                                    class="w-4 h-4"
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        d="M5 4a1 1 0 00-2 0v7.268a2 2 0 000 3.464V16a1 1 0 102 0v-1.268a2 2 0 000-3.464V4zM11 4a1 1 0 10-2 0v1.268a2 2 0 000 3.464V16a1 1 0 102 0V8.732a2 2 0 000-3.464V4zM16 3a1 1 0 011 1v7.268a2 2 0 010 3.464V16a1 1 0 11-2 0v-1.268a2 2 0 010-3.464V4a1 1 0 011-1z"
                                                    ></path>
                                                </svg>
                                            </button>
                                        </div>
                                        <input
                                            type="checkbox"
                                            class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                        />
                                    </div>
                                </div>
                                <div class="flex">
                                    <GamificationScoreCard
                                        :gamification-score-card="gamificationScoreCard"
                                        :id="`student_1`"
                                        :settings="cardSettings"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.activate_deactivate') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.use_health')" for="use_health" />
                            <input
                                name="selected"
                                v-model="form.use_health"
                                type="checkbox"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError :value="form.errors.use_health" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.use_experience')" for="use_experience" />
                            <input
                                name="selected"
                                v-model="form.use_experience"
                                type="checkbox"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError :value="form.errors.use_experience" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.use_coins')" for="use_coins" />
                            <input
                                name="selected"
                                v-model="form.use_coins"
                                type="checkbox"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError :value="form.errors.use_coins" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.use_energy')" for="use_energy" />
                            <input
                                name="selected"
                                v-model="form.use_energy"
                                type="checkbox"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError :value="form.errors.use_energy" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $tc('messages.permissions', 2) }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel
                                :value="$t('messages.students_can_upload_avatars')"
                                for="students_can_upload_avatars"
                            />
                            <input
                                name="selected"
                                v-model="form.students_can_upload_avatars"
                                id="students_can_upload_avatars"
                                type="checkbox"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError :value="form.errors.students_can_upload_avatars" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    <div>{{ $tc('messages.activities', 2) }}</div>
                    <div>
                        <GamificationActivityPointsPreview
                            :ranges="options.grade_ranges"
                            :weights="options.weights"
                            :settings="form"
                        />
                    </div>
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel
                                :value="$t('messages.is_experience_for_activity_active')"
                                for="is_experience_for_activity_active"
                            />
                            <input
                                id="is_experience_for_activity_active"
                                type="checkbox"
                                v-model="form.is_experience_for_activity_active"
                                name="is_experience_for_activity_active"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError :value="form.errors.is_experience_for_activity_active" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel
                                :value="$t('messages.is_experience_for_activity_reward_proportional')"
                                for="is_experience_for_activity_reward_proportional"
                            />
                            <input
                                id="is_experience_for_activity_reward_proportional"
                                type="checkbox"
                                v-model="form.is_experience_for_activity_reward_proportional"
                                name="is_experience_for_activity_reward_proportional"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError
                                :value="form.errors.is_experience_for_activity_reward_proportional"
                                class="mt-2"
                            />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel
                                :value="$t('messages.is_coins_for_activity_active')"
                                for="is_coins_for_activity_active"
                            />
                            <input
                                id="is_coins_for_activity_active"
                                type="checkbox"
                                v-model="form.is_coins_for_activity_active"
                                name="is_coins_for_activity_active"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError :value="form.errors.is_coins_for_activity_active" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel
                                :value="$t('messages.is_coins_for_activity_reward_proportional')"
                                for="is_coins_for_activity_reward_proportional"
                            />
                            <input
                                id="is_coins_for_activity_reward_proportional"
                                type="checkbox"
                                v-model="form.is_coins_for_activity_reward_proportional"
                                name="is_coins_for_activity_reward_proportional"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError :value="form.errors.is_coins_for_activity_reward_proportional" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.health_recovery') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel
                                :value="$t('messages.percent_health_recovery_attendance')"
                                for="percent_health_recovery_attendance"
                            />
                            <div class="flex space-x-2 items-center">
                                <div>%</div>
                                <FormInput
                                    required
                                    id="percent_health_recovery_attendance"
                                    v-model.number="form.percent_health_recovery_attendance"
                                    name="percent_health_recovery_attendance"
                                    type="number"
                                    min="0"
                                    max="100"
                                    class="mt-1"
                                />
                                <div>
                                    {{ Math.round((form.percent_health_recovery_attendance * form.health_max) / 100) }}
                                </div>
                            </div>
                            <FormError :value="form.errors.percent_health_recovery_attendance" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel
                                :value="$t('messages.percent_health_recovery_delay')"
                                for="percent_health_recovery_delay"
                            />
                            <div class="flex space-x-2 items-center">
                                <div>%</div>
                                <FormInput
                                    required
                                    id="percent_health_recovery_delay"
                                    v-model.number="form.percent_health_recovery_delay"
                                    name="percent_health_recovery_delay"
                                    type="number"
                                    min="0"
                                    max="100"
                                    class="mt-1"
                                />
                                <div>
                                    {{ Math.round((form.percent_health_recovery_delay * form.health_max) / 100) }}
                                </div>
                            </div>
                            <FormError :value="form.errors.percent_health_recovery_delay" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel
                                :value="$t('messages.percent_health_recovery_unjustified_absence')"
                                for="percent_health_recovery_unjustified_absence"
                            />
                            <div class="flex space-x-2 items-center">
                                <div>%</div>
                                <FormInput
                                    required
                                    id="percent_health_recovery_unjustified_absence"
                                    v-model.number="form.percent_health_recovery_unjustified_absence"
                                    name="percent_health_recovery_unjustified_absence"
                                    type="number"
                                    min="0"
                                    max="100"
                                    class="mt-1"
                                />
                                <div>
                                    {{
                                        Math.round(
                                            (form.percent_health_recovery_unjustified_absence * form.health_max) / 100,
                                        )
                                    }}
                                </div>
                            </div>
                            <FormError :value="form.errors.percent_health_recovery_unjustified_absence" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.game_health_loss_percent_for_foul') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel
                                :value="$t('messages.percent_health_loss_foul_mild')"
                                for="percent_health_loss_foul_mild"
                            />
                            <div class="flex space-x-2 items-center">
                                <div>%</div>
                                <FormInput
                                    required
                                    id="percent_health_loss_foul_mild"
                                    v-model.number="form.percent_health_loss_foul_mild"
                                    name="percent_health_loss_foul_mild"
                                    type="number"
                                    min="0"
                                    max="100"
                                    class="mt-1"
                                />
                            </div>
                            <FormError :value="form.errors.percent_health_loss_foul_mild" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel
                                :value="$t('messages.percent_health_loss_foul_serious')"
                                for="percent_health_loss_foul_serious"
                            />
                            <div class="flex space-x-2 items-center">
                                <div>%</div>
                                <FormInput
                                    required
                                    id="percent_health_loss_foul_serious"
                                    v-model.number="form.percent_health_loss_foul_serious"
                                    name="percent_health_loss_foul_serious"
                                    type="number"
                                    min="0"
                                    max="100"
                                    class="mt-1"
                                />
                            </div>
                            <FormError :value="form.errors.percent_health_loss_foul_serious" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel
                                :value="$t('messages.percent_health_loss_foul_very_serious')"
                                for="percent_health_loss_foul_very_serious"
                            />
                            <div class="flex space-x-2 items-center">
                                <div>%</div>
                                <FormInput
                                    required
                                    id="percent_health_loss_foul_very_serious"
                                    v-model.number="form.percent_health_loss_foul_very_serious"
                                    name="percent_health_loss_foul_very_serious"
                                    type="number"
                                    min="0"
                                    max="100"
                                    class="mt-1"
                                />
                            </div>
                            <FormError :value="form.errors.percent_health_loss_foul_very_serious" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.trimester_accelerator') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel
                                :value="$t('messages.is_trimester_accelerator_active')"
                                for="is_trimester_accelerator_active"
                            />
                            <input
                                id="is_trimester_accelerator_active"
                                type="checkbox"
                                v-model="form.is_trimester_accelerator_active"
                                name="is_trimester_accelerator_active"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError :value="form.errors.is_trimester_accelerator_active" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel
                                :value="$t('messages.is_trimester_accelerator_applied_to_rewards')"
                                for="is_trimester_accelerator_applied_to_rewards"
                            />
                            <input
                                id="is_trimester_accelerator_applied_to_rewards"
                                type="checkbox"
                                v-model="form.is_trimester_accelerator_applied_to_rewards"
                                name="is_trimester_accelerator_applied_to_rewards"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError :value="form.errors.is_trimester_accelerator_applied_to_rewards" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.game_speed') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.game_speed')" for="game_speed" />
                            <FormInput
                                required
                                id="game_speed"
                                v-model.number="form.game_speed"
                                name="game_speed"
                                type="number"
                                step="0.01"
                                min="0.50"
                                max="4.00"
                                class="mt-1"
                            />
                            <FormError :value="form.errors.game_speed" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel
                                :value="$t('messages.is_game_speed_applied_to_rewards')"
                                for="is_game_speed_applied_to_rewards"
                            />
                            <input
                                id="is_game_speed_applied_to_rewards"
                                type="checkbox"
                                v-model="form.is_game_speed_applied_to_rewards"
                                name="is_game_speed_applied_to_rewards"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError :value="form.errors.is_game_speed_applied_to_rewards" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    <div>
                        {{ $tc('messages.gamification_chests', 2) }}
                    </div>
                    <div class="mt-5 inline-block" v-show="form.is_chest_active">
                        <AppButton as="a" target="_blank" :href="meta.links.edit_chest_settings">
                            <svg
                                class="w-4 h-4 mr-1"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z"
                                ></path>
                                <path
                                    d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z"
                                ></path>
                            </svg>

                            {{ $t('messages.settings') }}
                        </AppButton>
                    </div>
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.is_chest_active')" for="is_chest_active" />
                            <input
                                id="is_chest_active"
                                type="checkbox"
                                v-model="form.is_chest_active"
                                name="is_chest_active"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError :value="form.errors.is_chest_active" class="mt-2" />
                        </div>
                    </div>

                    <details class="mt-3 sm:col-span-12">
                        <summary>
                            <svg
                                class="w-6 h-6 text-gray-400"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                    clip-rule="evenodd"
                                ></path>
                            </svg>
                        </summary>
                        <p v-text="$t('messages.chest_types_explanation')" class="whitespace-pre-line" />
                    </details>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $tc('messages.weekly_rewards', 1) }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.is_weekly_reward_active')" for="is_weekly_reward_active" />
                            <input
                                id="is_weekly_reward_active"
                                type="checkbox"
                                v-model="form.is_weekly_reward_active"
                                name="is_weekly_reward_active"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError :value="form.errors.is_weekly_reward_active" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel
                                :value="$t('messages.weekly_reward_coins_multiplier')"
                                for="weekly_reward_coins_multiplier"
                            />
                            <FormInput
                                required
                                id="weekly_reward_coins_multiplier"
                                v-model.number="form.weekly_reward_coins_multiplier"
                                name="weekly_reward_coins_multiplier"
                                type="number"
                                step="0.01"
                                min="0"
                                max="10.00"
                                class="mt-1"
                            />
                            {{ weeklyRewardCoins }}
                            <FormError :value="form.errors.weekly_reward_coins_multiplier" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel
                                :value="$t('messages.weekly_reward_experience_multiplier')"
                                for="weekly_reward_experience_multiplier"
                            />
                            <FormInput
                                required
                                id="weekly_reward_experience_multiplier"
                                v-model.number="form.weekly_reward_experience_multiplier"
                                name="weekly_reward_experience_multiplier"
                                type="number"
                                step="0.01"
                                min="0"
                                max="10.00"
                                class="mt-1"
                            />
                            {{ weeklyRewardExperience }}
                            <FormError :value="form.errors.weekly_reward_experience_multiplier" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    <div class="mt-1">
                        <FormToggle name="is_fainting_active" v-model="form.is_fainting_active" type="checkbox" />
                        <FormLabel :value="$t('messages.is_fainting_active')" for="is_fainting_active" />
                        <FormError :value="form.errors.is_fainting_active" class="mt-2" />
                        <div class="text-gray-500 text-xs">({{ $t('messages.fainting_state_explanation') }})</div>
                    </div>
                </template>
                <div
                    :class="{
                        'opacity-50 pointer-events-none': !form.is_fainting_active,
                    }"
                >
                    <div class="mt-1">
                        <FormLabel
                            :value="$t('messages.is_fainting_attendance_active')"
                            for="is_fainting_attendance_active"
                        />
                        <FormToggle name="is_fainting_attendance_active" v-model="form.is_fainting_attendance_active" />
                        <FormError :value="form.errors.is_fainting_attendance_active" class="mt-2" />
                    </div>

                    <div class="mt-1">
                        <FormLabel :value="$t('messages.is_fainting_chest_active')" for="is_fainting_chest_active" />
                        <FormToggle name="is_fainting_chest_active" v-model="form.is_fainting_chest_active" />
                        <FormError :value="form.errors.is_fainting_chest_active" class="mt-2" />
                    </div>

                    <div class="mt-1">
                        <FormLabel
                            :value="$t('messages.is_fainting_event_participation_active')"
                            for="is_fainting_event_participation_active"
                        />
                        <FormToggle
                            name="is_fainting_event_participation_active"
                            v-model="form.is_fainting_event_participation_active"
                        />
                        <FormError :value="form.errors.is_fainting_event_participation_active" class="mt-2" />
                    </div>

                    <div class="mt-1">
                        <FormLabel
                            :value="$t('messages.is_fainting_use_items_active')"
                            for="is_fainting_use_items_active"
                        />
                        <FormToggle name="is_fainting_use_items_active" v-model="form.is_fainting_use_items_active" />
                        <FormError :value="form.errors.is_fainting_use_items_active" class="mt-2" />
                    </div>

                    <div class="mt-1">
                        <FormLabel
                            :value="$t('messages.is_fainting_purchases_active')"
                            for="is_fainting_purchases_active"
                        />
                        <FormToggle name="is_fainting_purchases_active" v-model="form.is_fainting_purchases_active" />
                        <FormError :value="form.errors.is_fainting_purchases_active" class="mt-2" />
                    </div>
                </div>
            </TitleFieldsFormSection>

            <div class="flex justify-end mt-5">
                <AppButton desired-action element="button" type="submit" :disabled="form.processing">
                    {{ $t('messages.save') }}
                    <span v-if="form.processing" class="ml-1 btn-spinner"></span>
                </AppButton>
            </div>
        </form>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import AppButton from '@/components/AppButton.vue';
import BattleTerrainsSelectorPanel from '@/components/BattleTerrainsSelectorPanel.vue';
import GamificationScoreCard from '@/components/Gamification/GamificationScoreCard.vue';
import GamificationActivityPointsPreview from '@/components/pageSpecific/GamificationActivityPointsPreview.vue';
import FormToggle from '../../../../../components/Forms/FormToggle.vue';

export default {
    components: {
        FormToggle,
        GamificationActivityPointsPreview,
        PanelPage,
        TitleFieldsFormSection,
        FormInput,
        FormLabel,
        FormError,
        AppButton,
        BattleTerrainsSelectorPanel,
        GamificationScoreCard,
    },
    name: 'Edit',
    layout: Layout,
    props: ['meta', 'options', 'gamificationSet'],
    data() {
        return {
            form: this.$inertia.form({
                title: this.gamificationSet.title,
                base: this.gamificationSet.base,
                ratio: this.gamificationSet.ratio,
                health_max: this.gamificationSet.health_max,
                show_level: this.gamificationSet.show_level,
                show_experience: this.gamificationSet.show_experience,
                show_coins: this.gamificationSet.show_coins,
                show_health: this.gamificationSet.show_health,
                use_health: this.gamificationSet.use_health,
                use_experience: this.gamificationSet.use_experience,
                use_coins: this.gamificationSet.use_coins,
                use_energy: this.gamificationSet.use_energy,

                students_can_upload_avatars: this.gamificationSet.students_can_upload_avatars,

                percent_health_loss_foul_mild: this.gamificationSet.percent_health_loss_foul_mild,
                percent_health_loss_foul_serious: this.gamificationSet.percent_health_loss_foul_serious,
                percent_health_loss_foul_very_serious: this.gamificationSet.percent_health_loss_foul_very_serious,

                coins_amount_base: this.gamificationSet.coins_amount_base,
                is_coins_for_activity_active: this.gamificationSet.is_coins_for_activity_active,
                is_coins_for_activity_reward_proportional:
                    this.gamificationSet.is_coins_for_activity_reward_proportional,
                is_experience_for_activity_active: this.gamificationSet.is_experience_for_activity_active,
                is_experience_for_activity_reward_proportional:
                    this.gamificationSet.is_experience_for_activity_reward_proportional,
                students_currency_graph_days_span: this.gamificationSet.students_currency_graph_days_span,
                percent_health_recovery_attendance: this.gamificationSet.percent_health_recovery_attendance,
                percent_health_recovery_delay: this.gamificationSet.percent_health_recovery_delay,
                percent_health_recovery_unjustified_absence:
                    this.gamificationSet.percent_health_recovery_unjustified_absence,
                is_trimester_accelerator_active: this.gamificationSet.is_trimester_accelerator_active,
                is_trimester_accelerator_applied_to_rewards:
                    this.gamificationSet.is_trimester_accelerator_applied_to_rewards,
                game_speed: this.gamificationSet.game_speed,
                is_game_speed_applied_to_rewards: this.gamificationSet.is_game_speed_applied_to_rewards,
                is_chest_active: this.gamificationSet.is_chest_active,
                is_weekly_reward_active: this.gamificationSet.is_weekly_reward_active,
                weekly_reward_coins_multiplier: this.gamificationSet.weekly_reward_coins_multiplier,
                weekly_reward_experience_multiplier: this.gamificationSet.weekly_reward_experience_multiplier,
                battle_terrain_id: this.gamificationSet.battle_terrain_id,
                is_fainting_active: this.gamificationSet.is_fainting_active,
                is_fainting_attendance_active: this.gamificationSet.is_fainting_attendance_active,
                is_fainting_chest_active: this.gamificationSet.is_fainting_chest_active,
                is_fainting_event_participation_active: this.gamificationSet.is_fainting_event_participation_active,
                is_fainting_use_items_active: this.gamificationSet.is_fainting_use_items_active,
                is_fainting_purchases_active: this.gamificationSet.is_fainting_purchases_active,
            }),
            gamificationScoreCard: {
                id: null,
                student_id: null,
                min: 50,
                max: 110,
                width: 60,
                rank: 'Recruit',
                experience_total: 90,
                health_total: 150,
                health_max: 200,
                health_percent: 75,
                coins_total: 1250,
                coins_short_display: '1.25k',
                current_level: 1,
                percent: 66,
                current_level_experience_points: 40,
                particles: { batch: null, experience: null },
                gamification_character_type_id: null,
                show_faint_icon: false,
                links: {
                    currency_points_show: this.meta.links.set_show,
                },
            },
        };
    },
    methods: {
        submit() {
            this.form.patch(this.meta.links.update);
        },
    },
    computed: {
        cardSettings: function () {
            return {
                show_level: this.form.show_level,
                show_experience: this.form.show_experience,
                show_health: this.form.show_health,
                show_coins: this.form.show_coins,
                use_coins: this.form.use_coins,
                use_experience: this.form.use_experience,
                use_health: this.form.use_health,
            };
        },
        weeklyRewardExperience: function () {
            return Math.round(this.baseExperienceWithMultipliers * this.form.weekly_reward_experience_multiplier);
        },
        weeklyRewardCoins: function () {
            return Math.round(this.baseCoinsWithMultipliers * this.form.weekly_reward_coins_multiplier);
        },
        baseExperienceWithMultipliers: function () {
            const trimester_speed = 1.2;
            return Math.round(this.form.base * this.form.game_speed * trimester_speed); //TODO: get trimester from settings
        },
        baseCoinsWithMultipliers: function () {
            const trimester_speed = 1.2;
            return Math.round(this.form.coins_amount_base * this.form.game_speed * trimester_speed); //TODO: get trimester from settings
        },
    },
};
</script>
<style scoped>
details summary::-webkit-details-marker,
details summary::marker {
    display: none;
    content: '';
}
</style>
